import type { ThreadMessage } from '@assistant-ui/react';

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function firstUrlString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    const trimmed = value.trim();
    try {
      // eslint-disable-next-line no-new
      new URL(trimmed);
      return trimmed;
    } catch {
      return null;
    }
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstUrlString(item);
      if (found) return found;
    }
  }
  return null;
}

function urlFromToolResult(result: unknown): string | null {
  let parsed: unknown = result;
  if (typeof result === 'string' && result.trim()) {
    try {
      parsed = JSON.parse(result);
    } catch {
      return null;
    }
  }
  const row = asRecord(parsed);
  if (!row) return null;

  if (row.phase === 'answers') {
    const answers = asRecord(row.answers);
    const fromAnswers = firstUrlString(answers?.url);
    if (fromAnswers) return fromAnswers;
  }

  if (row.phase === 'api_result') {
    const request = asRecord(row.request);
    const fromRequest = firstUrlString(request?.url);
    if (fromRequest) return fromRequest;
  }

  return firstUrlString(row.url);
}

function urlFromToolArgs(args: unknown): string | null {
  const row = asRecord(args);
  if (!row) return null;
  const overview = asRecord(row.overview);
  const fromOverview = firstUrlString(overview?.url);
  if (fromOverview) return fromOverview;
  return firstUrlString(row.url);
}

/**
 * Latest page URL used by a prior skill run in this chat thread.
 */
export function findPriorSkillUrlFromMessages(
  messages: readonly ThreadMessage[]
): string | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (!Array.isArray(message.content)) continue;

    for (const part of message.content) {
      if (!part || typeof part !== 'object' || !('type' in part)) continue;
      const row = part as {
        type?: string;
        toolName?: string;
        args?: unknown;
        result?: unknown;
      };
      if (row.type !== 'tool-call') continue;

      const name = row.toolName ?? '';
      if (name === 'present_skill_overview') {
        const fromArgs = urlFromToolArgs(row.args);
        if (fromArgs) return fromArgs;
      }

      if (name.startsWith('configure_') || name.startsWith('skill_')) {
        const fromResult = urlFromToolResult(row.result);
        if (fromResult) return fromResult;
        const fromArgs = urlFromToolArgs(row.args);
        if (fromArgs) return fromArgs;
      }
    }
  }
  return null;
}

export const URL_REUSE_STEP_ID = '_url_reuse';
export const URL_REUSE_SAME = 'same';
export const URL_REUSE_NEW = 'new';
